// PassPreview — HTML/CSS approximation of the Google Wallet loyalty pass
// front (slice 4a-3). Pure presentational: it takes program design data as
// props and renders, nothing more. Lives in `shared/` because 4a-4's customer
// enrollment page and (possibly) the admin tenant detail view reuse it.
//
// It is NOT a pixel-exact Google Wallet render — it's a faithful-enough
// preview so an owner building their program can see the layout update live.
//
// Layout (top → bottom), matching the live Google Wallet render:
//   header        logo + business name
//   title         "Loyalty Card"
//   next reward   "Next reward at stamp N" + reward text
//   detail row    owner-chosen customer fields (e.g. Birthday, Email)
//   qr            placeholder QR (real per-card QR is generated per card)
//   code          card code under the QR
//   bottom band   stamp grid in a darker bottom band — visually anchors the pass

import { memo, useEffect, useState } from 'react'
import type { BackLink, OnboardingFieldKey } from '@naya/shared'

import { EnrollmentPreview } from './EnrollmentPreview.js'

import {
  StampIcon,
  type StampIconState,
} from '../../assets/stamp-icons/index.js'
import dummyQr from './dummy-qr.svg'
import './passPreview.css'

/** One label/value pair in the pass-front detail row. */
export interface PassPreviewDetail {
  label: string
  /** The value to show. In the builder preview this is dummy text. */
  value: string
}

/** A reward line under the stamp grid (next milestone or grand prize). */
export interface PassPreviewReward {
  /** e.g. "Next at stamp 4" or "Grand prize · stamp 10". */
  label: string
  /** The reward text. Falls back to a placeholder when blank. */
  text: string
}

export interface PassPreviewProgram {
  /** Business name — shown in the header and footer. */
  businessName: string
  /** Program name — the big headline under the business name. Defaults to
   *  "Rewards" for new programs; owners can customize in the builder. */
  programName: string
  /** Logo URL (already cache-busted by the caller if needed). Optional. */
  logoUrl: string | null
  totalStamps: number
  /** How many stamps to render as "filled". Preview default is 0. */
  filledCount?: number
  primaryColor: string
  textColor: string
  /** Tint applied to built-in stamp icons. Owner-configurable in step 3 so
   *  stamp color can be picked independently of text color. */
  stampColor: string
  /** pass_design.stamp_filled_url / stamp_empty_url — refs or image URLs. */
  stampFilledRef: string
  stampEmptyRef: string
  /** Owner-chosen customer fields for the detail row. Empty = no row. */
  detailFields: PassPreviewDetail[]
  /** The next unearned milestone, if any (omitted when all are earned). */
  nextMilestone?: PassPreviewReward
  /** The grand prize reward line. Always shown. */
  grandPrize: PassPreviewReward
  /**
   * Stamp positions (1-indexed) that earn a reward — both intermediate
   * milestones and the grand prize. Empty slots at these positions render
   * with a small star-badge overlay so the customer can see "earn this stamp
   * to get a reward" at a glance. Filled slots don't get the badge: the
   * reward is already in-hand (or claimable from the rewards list).
   */
  milestoneStamps: number[]
  /**
   * Secret menu — stamp positions (1-indexed) tagged off-menu this cycle.
   * Unlike `milestoneStamps`, this only decorates FILLED slots (an empty
   * slot has no history yet, so there's nothing to badge) — matches the
   * server-side hero generator's rule. The badge shows the stamp's 1-indexed
   * order within this array (sorted ascending), not the stamp's grid
   * position, so a card with 2 secret stamps shows "1" and "2" regardless of
   * which grid cells they landed in. The builder preview has no real card
   * history, so `previewMapper.ts` always passes `[]` here — this is a
   * template preview, not a real customer's card.
   */
  secretStamps: number[]
  /**
   * Full reward ladder for the back-of-pass "All rewards" row. Each entry
   * mirrors a milestone in the program (or the card's frozen snapshot,
   * once a real card is rendered). Order is irrelevant — the renderer
   * sorts; we accept the caller's order as-is. The preview never marks
   * entries as "claimed" — the live wallet pass shows ✓ prefixes based on
   * cycle-scoped redemption state, which the current callers (builder,
   * enrollment) don't have. A future "view customer card" surface could
   * extend this to carry claimed positions.
   */
  rewards: Array<{ at_stamp: number; reward_text: string }>
  /** Label rendered next to the stamp count (default "Stamps"). */
  pointsLabel: string
  /** Optional descriptive blurb shown below the rewards block. */
  passDescription: string
  /**
   * Slice 4b — label shown above the customer's name on the front of the
   * pass (e.g. "Member · Juan"). Maps to `pass_design.account_name_label`.
   */
  accountNameLabel: string
  /**
   * Slice 4b — dummy customer name for the preview front. The owner has no
   * real customer at design time; the caller fills this with stock PH
   * placeholder ("Juan") or the real first name when previewing a card.
   */
  customerName: string
  /**
   * Customer email — shown on the back of the pass directly under the
   * Member name row (matches the live pass's `member-email` textModule).
   * The builder preview fills this with the dummy placeholder.
   */
  customerEmail: string
  /**
   * Slice 4b — back-of-pass content. `backLinks` becomes tappable rows on
   * the wallet pass back; `ownerTerms` becomes the "House rules" block.
   * Both empty = the back preview just shows system policies.
   */
  backLinks: BackLink[]
  ownerTerms: string
  /**
   * The enabled onboarding fields, in canonical ALL_ONBOARDING_FIELDS order,
   * each with its required flag. Drives the "Enroll" tab — a read-only mock
   * of the customer's enrollment screen showing exactly which fields will
   * appear and which are required. Empty list = the form will collect
   * nothing (V1 lower bound is "consent only").
   */
  enrollmentFields: Array<{
    key: OnboardingFieldKey
    label: string
    required: boolean
  }>
  /** Optional owner-uploaded hero photo URL (R2). When set, the preview's
   *  bottom band shows the photo behind the stamp grid. Null = solid color
   *  band (the default). */
  heroImageUrl?: string | null
  /** Brightness adjustment applied to the hero photo in the preview, integer
   *  in [-70, 0]. Approximated client-side via CSS filter — the live wallet
   *  pass applies it server-side, so the rendered output may differ slightly. */
  heroBrightness?: number
  /**
   * SLICE-REFERRAL — back-of-pass referral block. null/undefined = no block
   * (feature off, or this program isn't referral-enabled). `textRows` are the
   * code + status + welcome lines (mirrors the live pass's referral
   * textModules); `shareUrl` is the tappable share-link row. The builder
   * preview fills representative dummy values so the owner sees the layout.
   */
  referral?: {
    textRows: Array<{ label: string; value: string }>
    shareUrl: string | null
  } | null
  /**
   * SLICE-MEMBERSHIP-PASS — pass-type discriminant. Defaults to 'loyalty'
   * (omitted by the loyalty mapper, so loyalty rendering is byte-identical).
   * When 'membership', the front drops the stamp grid + "n / total" status
   * line and shows the tier / member number instead, and the back shows the
   * benefits list. `membership` carries the membership-specific content.
   */
  passType?: 'loyalty' | 'membership' | 'event_ticket'
  membership?: {
    /** Program tier ("Gold", "VIP"), or null for a single-level membership. */
    tierName: string | null
    /** Owner-chosen label for the member's ID ("Member No."). */
    memberNumberLabel: string
    /** The member's number — dummy placeholder in the builder preview. */
    memberNumber: string
    /** "Member since" date string — dummy placeholder in the builder preview. */
    memberSince: string
    /** Full-date expiry ("Oct 10, 2026") shown on the front + back, or null
     *  when the program never expires. */
    validUntil: string | null
    /** Back-of-pass perks list. */
    benefits: string[]
  } | null
  /** SLICE-EVENT-TICKET — present only when passType === 'event_ticket'. The
   *  front drops the stamp grid and shows event name / when / where / tier. */
  eventTicket?: {
    eventName: string
    /** Formatted PH-local date+time ("Sat, Jun 20, 7:00 PM"). */
    whenText: string
    venueName: string
    tierName: string
    /** Free-text seat line, or null for general admission. */
    seatText: string | null
    /** Human-readable ticket number — dummy in the builder preview. */
    ticketNumber: string
  } | null
}

function PassPreviewImpl({
  program,
  size = 'compact',
  desiredSide,
}: {
  program: PassPreviewProgram
  /** `compact` for the wizard side panel / sheet; `large` for the review step. */
  size?: 'compact' | 'large'
  /**
   * When set, switches the preview to this side whenever it changes. The
   * owner can still toggle freely afterward via the Front/Back tabs — this
   * only nudges the side on a step change (the builder uses it to follow the
   * "front of pass" / "back of pass" config steps). Leave undefined for
   * surfaces that don't track a step (review screen, admin tenant detail).
   */
  desiredSide?: 'front' | 'back' | 'enroll' | undefined
}) {
  const {
    businessName,
    programName,
    logoUrl,
    totalStamps,
    filledCount = 0,
    primaryColor,
    textColor,
    stampColor,
    stampFilledRef,
    stampEmptyRef,
    detailFields,
    nextMilestone,
    grandPrize,
    milestoneStamps,
    secretStamps,
    rewards,
    passDescription,
    accountNameLabel,
    customerName,
    customerEmail,
    backLinks,
    ownerTerms,
    enrollmentFields,
    heroImageUrl,
    heroBrightness = 0,
    referral,
    passType = 'loyalty',
    membership,
    eventTicket,
  } = program
  const isMembership = passType === 'membership'
  const isEventTicket = passType === 'event_ticket'
  // Note: `pointsLabel` is intentionally not destructured. The back-of-pass
  // "Card progress" row that used it was removed (the live pass shows the
  // stamp count once, via Google's loyaltyPoints block). The field stays on
  // PassPreviewProgram for the wallet builders + any future surface.

  // Slice 4b — flip-card toggle. Defaults to the front; clicking the toggle
  // shows the back-of-pass content (links + house rules + system policies).
  // The toggle state is local — the owner never persists "I prefer the back
  // view"; it's a transient affordance for previewing.
  // `side` is tri-state: 'front'/'back' show the wallet pass (with its
  // pass-shaped container chrome); 'enroll' swaps the entire container for an
  // approximation of the customer-facing enrollment screen. desiredSide can
  // hit any of the three so the wizard can auto-open enroll on the sign-up-
  // info step (mirroring how 'back' auto-opens on the back-config step).
  const [side, setSide] = useState<'front' | 'back' | 'enroll'>(
    desiredSide ?? 'front',
  )

  // Follow `desiredSide` when the caller changes it (builder step navigation).
  // This doesn't lock the tabs — the owner can still flip manually; the next
  // step change just re-syncs.
  useEffect(() => {
    if (desiredSide) setSide(desiredSide)
  }, [desiredSide])

  const milestoneSet = new Set(milestoneStamps)
  // Secret menu — order (1-indexed) within the sorted secret-stamp list, so
  // the badge shows "1", "2", … in banking order rather than the stamp's
  // grid position. Mirrors stamp-grid.ts's `ordinal` parameter to
  // renderSecretBadge.
  const secretOrdinal = new Map(
    [...secretStamps].sort((a, b) => a - b).map((stampNumber, i) => [stampNumber, i + 1]),
  )

  // The stamp grid: `totalStamps` slots, first `filledCount` filled. Milestone
  // slots get a star-badge overlay regardless of fill state so the customer
  // keeps seeing which stamps unlock a reward even after they've earned them
  // (matches the same rule in stamp-grid.ts so HTML preview and hero PNG
  // agree).
  const slots = Array.from({ length: totalStamps }, (_, i) => {
    const stampNumber = i + 1
    const state: StampIconState = i < filledCount ? 'filled' : 'empty'
    const showBadge = milestoneSet.has(stampNumber)
    // Secret menu — unlike the milestone badge, only decorates FILLED slots
    // (there's no "off-menu" history on a stamp that hasn't happened yet).
    const secretOrder =
      state === 'filled' ? secretOrdinal.get(stampNumber) : undefined
    return (
      <span
        className={`pass-preview-stamp${showBadge ? ' has-milestone' : ''}`}
        key={i}
      >
        <StampIcon
          refValue={state === 'filled' ? stampFilledRef : stampEmptyRef}
          state={state}
          className="pass-preview-stamp-icon"
        />
        {showBadge && (
          <svg
            className="pass-preview-stamp-badge"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            {/* Filled star — bright accent on a small contrasting disc. */}
            <circle cx="12" cy="12" r="11" fill="#FFD166" />
            <path
              d="M12 5l2.27 4.6 5.08.74-3.67 3.58.87 5.06L12 16.6l-4.55 2.38.87-5.06L4.65 10.34l5.08-.74L12 5z"
              fill="#1A1A1A"
            />
          </svg>
        )}
        {secretOrder != null && (
          // Bottom-left diamond silhouette — a different corner AND shape
          // (not just color) from the milestone star's disc, so a stamp
          // that's both shows both badges at once with no ambiguity.
          // Mirrors renderSecretBadge in
          // apps/api/src/services/wallet/stamp-grid.ts.
          <svg
            className="pass-preview-stamp-secret-badge"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              d="M12 1.5 L22.5 12 L12 22.5 L1.5 12 Z"
              fill="#6B3FA0"
              stroke="#1A1A1A"
              strokeWidth="1"
            />
            <text
              x="12"
              y="15.9"
              textAnchor="middle"
              fontFamily="sans-serif"
              fontSize={secretOrder >= 10 ? 8 : 9.5}
              fontWeight="700"
              fill="#FFFFFF"
            >
              {secretOrder}
            </text>
          </svg>
        )}
      </span>
    )
  })

  return (
    <div className="pass-preview-wrap">
      <div
        className="pass-preview-tabs"
        role="tablist"
        aria-label="Customer view"
      >
        <button
          type="button"
          role="tab"
          aria-selected={side === 'front'}
          className={`pass-preview-tab${side === 'front' ? ' is-active' : ''}`}
          onClick={() => setSide('front')}
        >
          Front
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={side === 'back'}
          className={`pass-preview-tab${side === 'back' ? ' is-active' : ''}`}
          onClick={() => setSide('back')}
        >
          Back
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={side === 'enroll'}
          className={`pass-preview-tab${side === 'enroll' ? ' is-active' : ''}`}
          onClick={() => setSide('enroll')}
        >
          Enroll
        </button>
      </div>

      {side === 'enroll' && (
        <EnrollmentPreview
          businessName={businessName}
          programName={programName}
          branchName={businessName}
          logoUrl={logoUrl}
          fields={enrollmentFields}
          referralEnabled={referral != null}
        />
      )}
      {side !== 'enroll' && (

      <div
        className={`pass-preview pass-preview-${size}`}
        style={
          {
            '--pass-primary': primaryColor,
            '--pass-text': textColor,
            '--pass-stamp': stampColor,
          } as React.CSSProperties
        }
        role="img"
        aria-label={`Preview of the ${businessName} ${
          isMembership ? 'membership' : 'loyalty'
        } pass — ${side}`}
      >
        {side === 'front' && isEventTicket ? (
          <EventTicketFront
            logoUrl={logoUrl}
            businessName={businessName}
            programName={programName}
            eventName={eventTicket?.eventName ?? ''}
            whenText={eventTicket?.whenText ?? ''}
            venueName={eventTicket?.venueName ?? ''}
            tierName={eventTicket?.tierName ?? ''}
            seatText={eventTicket?.seatText ?? null}
            ticketNumber={eventTicket?.ticketNumber ?? ''}
            heroImageUrl={heroImageUrl ?? null}
            heroBrightness={heroBrightness}
          />
        ) : side === 'front' && isMembership ? (
          <MembershipFront
            logoUrl={logoUrl}
            businessName={businessName}
            programName={programName}
            accountNameLabel={accountNameLabel}
            customerName={customerName}
            tierName={membership?.tierName ?? null}
            memberNumber={membership?.memberNumber ?? ''}
            validUntil={membership?.validUntil ?? null}
            detailFields={detailFields}
            heroImageUrl={heroImageUrl ?? null}
            heroBrightness={heroBrightness}
          />
        ) : side === 'front' ? (
          <>
            {/* Top section sits on the primary color; the stamp grid drops
                into a darker bottom band, matching the live Wallet render. */}
            <div className="pass-preview-top">
              {/* header */}
              <div className="pass-preview-header">
                <span className="pass-preview-logo">
                  {logoUrl ? (
                    <img src={logoUrl} alt="" />
                  ) : (
                    <span
                      className="pass-preview-logo-fallback"
                      aria-hidden="true"
                    />
                  )}
                </span>
                <span className="pass-preview-business">
                  {businessName || 'Your business'}
                </span>
              </div>

              {/* title — Google renders the program name (default "Rewards",
                  owner-editable in the builder) as a large headline. */}
              <div className="pass-preview-title">
                {programName || 'Rewards'}
              </div>

              {/* Row 1 — locked: customer's first name + the stamp count.
                  Renders on Google Wallet via classTemplateInfo as a twoItems
                  row (object.accountName + object.loyaltyPoints). The visual
                  stamp grid below shows the same count graphically; the text
                  cell is the scannable number. */}
              <div className="pass-preview-row pass-preview-row-primary">
                <div className="pass-preview-detail">
                  <span className="pass-preview-detail-label">
                    {accountNameLabel}
                  </span>
                  <span className="pass-preview-detail-value">
                    {customerName}
                  </span>
                </div>
                <div className="pass-preview-detail">
                  <span className="pass-preview-detail-label">
                    Stamps
                  </span>
                  <span className="pass-preview-detail-value">
                    {filledCount} / {totalStamps}
                  </span>
                </div>
              </div>

              {/* primary reward line — Google surfaces the next unearned
                  milestone (or grand prize when none remain) directly under
                  the title. */}
              {(nextMilestone ?? grandPrize) && (
                <div className="pass-preview-next">
                  <span className="pass-preview-next-label">
                    {(nextMilestone ?? grandPrize).label}
                  </span>
                  <span className="pass-preview-next-text">
                    {(nextMilestone ?? grandPrize).text.trim() ||
                      'Your reward appears here'}
                  </span>
                </div>
              )}

              {/* detail row — owner-chosen customer fields */}
              {detailFields.length > 0 && (
                <div className="pass-preview-details">
                  {detailFields.map((d) => (
                    <div className="pass-preview-detail" key={d.label}>
                      <span className="pass-preview-detail-label">{d.label}</span>
                      <span className="pass-preview-detail-value">{d.value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* QR — static placeholder; the real per-card QR is generated
                  per-card. Card code shows below via barcode.alternateText. */}
              <div className="pass-preview-qr">
                <img src={dummyQr} alt="" className="pass-preview-qr-img" />
              </div>
              <div className="pass-preview-qr-altext">GTC-2J5K-9P3M</div>
            </div>

            {/* bottom band: stamp grid. When the owner has uploaded a hero
                photo, it sits behind the grid (with the saved brightness
                approximation via CSS filter). */}
            <div
              className={`pass-preview-band${heroImageUrl ? ' has-hero' : ''}`}
            >
              {heroImageUrl && (
                <img
                  src={heroImageUrl}
                  alt=""
                  className="pass-preview-hero"
                  style={{
                    filter: `brightness(${1 + heroBrightness / 100})`,
                  }}
                  aria-hidden="true"
                />
              )}
              <div className="pass-preview-grid">{slots}</div>
            </div>
          </>
        ) : (
          <BackOfPass
            logoUrl={logoUrl}
            programName={programName}
            accountNameLabel={accountNameLabel}
            customerName={customerName}
            customerEmail={customerEmail}
            passDescription={passDescription}
            backLinks={backLinks}
            ownerTerms={ownerTerms}
            nextMilestone={nextMilestone ?? grandPrize}
            rewards={rewards}
            detailFields={detailFields}
            referral={referral ?? null}
            membership={isMembership ? (membership ?? null) : null}
          />
        )}
      </div>
      )}
    </div>
  )
}

// SLICE-MEMBERSHIP-PASS — the membership pass front. Same chrome as the
// loyalty front (header + title + primary row + detail row + QR), but the
// primary row carries the member name + the tier (not the stamp count), there
// is a member-number row, and there is NO stamp-grid bottom band and NO
// "n / total" status line. A hero photo, when set, sits behind the lower
// content instead of behind a stamp grid.
function MembershipFront({
  logoUrl,
  businessName,
  programName,
  accountNameLabel,
  customerName,
  tierName,
  memberNumber,
  validUntil,
  detailFields,
  heroImageUrl,
  heroBrightness,
}: {
  logoUrl: string | null
  businessName: string
  programName: string
  accountNameLabel: string
  customerName: string
  tierName: string | null
  memberNumber: string
  validUntil: string | null
  detailFields: PassPreviewDetail[]
  heroImageUrl: string | null
  heroBrightness: number
}) {
  return (
    <>
      <div className="pass-preview-top">
        {/* header */}
        <div className="pass-preview-header">
          <span className="pass-preview-logo">
            {logoUrl ? (
              <img src={logoUrl} alt="" />
            ) : (
              <span className="pass-preview-logo-fallback" aria-hidden="true" />
            )}
          </span>
          <span className="pass-preview-business">
            {businessName || 'Your business'}
          </span>
        </div>

        {/* title — the program name headline (default "Membership"). */}
        <div className="pass-preview-title">{programName || 'Membership'}</div>

        {/* Primary row — member name + tier (the membership analogue of the
            loyalty name + stamp-count row). The tier cell is omitted when the
            membership has no tier. */}
        <div className="pass-preview-row pass-preview-row-primary">
          <div className="pass-preview-detail">
            <span className="pass-preview-detail-label">{accountNameLabel}</span>
            <span className="pass-preview-detail-value">{customerName}</span>
          </div>
          {tierName && (
            <div className="pass-preview-detail">
              <span className="pass-preview-detail-label">Tier</span>
              <span className="pass-preview-detail-value">{tierName}</span>
            </div>
          )}
        </div>

        {/* Valid-until — full date on the front, mirroring the live pass's
            front expiry row. Only when the program expires (item 3). */}
        {validUntil && (
          <div className="pass-preview-details">
            <div className="pass-preview-detail">
              <span className="pass-preview-detail-label">Valid until</span>
              <span className="pass-preview-detail-value">{validUntil}</span>
            </div>
          </div>
        )}

        {/* The member number is printed under the QR (altText) + on the back —
            not repeated as a front row. */}

        {/* detail row — owner-chosen customer fields */}
        {detailFields.length > 0 && (
          <div className="pass-preview-details">
            {detailFields.map((d) => (
              <div className="pass-preview-detail" key={d.label}>
                <span className="pass-preview-detail-label">{d.label}</span>
                <span className="pass-preview-detail-value">{d.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* QR — static placeholder; the real per-card QR is generated per-card.
            The member number is the altText under the QR (its only spot on the
            front). */}
        <div className="pass-preview-qr">
          <img src={dummyQr} alt="" className="pass-preview-qr-img" />
        </div>
        <div className="pass-preview-qr-altext">{memberNumber}</div>
      </div>

      {/* Optional hero banner — no stamp grid for a membership, so it renders
          as a full-width image band (the `pass-preview-membership-banner`
          height keeps it a proper banner, not a thin sliver). Only when a hero
          photo is set; otherwise the solid primary color fills the pass. */}
      {heroImageUrl && (
        <div className="pass-preview-band has-hero pass-preview-membership-banner">
          <img
            src={heroImageUrl}
            alt=""
            className="pass-preview-hero"
            style={{ filter: `brightness(${1 + heroBrightness / 100})` }}
            aria-hidden="true"
          />
        </div>
      )}
    </>
  )
}

// SLICE-EVENT-TICKET — the event-ticket front. No stamp grid: event name as the
// headline, then when / where / tier / seat rows, and the ticket number under
// the QR. Mirrors the native eventTicket layout (Apple) + the EventTicketObject
// fields (Google) at the preview's approximation level.
function EventTicketFront({
  logoUrl,
  businessName,
  programName,
  eventName,
  whenText,
  venueName,
  tierName,
  seatText,
  ticketNumber,
  heroImageUrl,
  heroBrightness,
}: {
  logoUrl: string | null
  businessName: string
  programName: string
  eventName: string
  whenText: string
  venueName: string
  tierName: string
  seatText: string | null
  ticketNumber: string
  heroImageUrl: string | null
  heroBrightness: number
}) {
  return (
    <>
      <div className="pass-preview-top">
        <div className="pass-preview-header">
          <span className="pass-preview-logo">
            {logoUrl ? (
              <img src={logoUrl} alt="" />
            ) : (
              <span className="pass-preview-logo-fallback" aria-hidden="true" />
            )}
          </span>
          <span className="pass-preview-business">
            {businessName || 'Your business'}
          </span>
        </div>

        {/* Event name is the headline (falls back to the program name). */}
        <div className="pass-preview-title">
          {eventName || programName || 'Event'}
        </div>

        {/* Tier as the prominent line + the date/time alongside it. */}
        <div className="pass-preview-row pass-preview-row-primary">
          {tierName && (
            <div className="pass-preview-detail">
              <span className="pass-preview-detail-label">Tier</span>
              <span className="pass-preview-detail-value">{tierName}</span>
            </div>
          )}
          <div className="pass-preview-detail">
            <span className="pass-preview-detail-label">When</span>
            <span className="pass-preview-detail-value">{whenText || '—'}</span>
          </div>
        </div>

        {/* Where (+ seat when assigned). */}
        <div className="pass-preview-details">
          <div className="pass-preview-detail">
            <span className="pass-preview-detail-label">Where</span>
            <span className="pass-preview-detail-value">{venueName || '—'}</span>
          </div>
          {seatText && (
            <div className="pass-preview-detail">
              <span className="pass-preview-detail-label">Seat</span>
              <span className="pass-preview-detail-value">{seatText}</span>
            </div>
          )}
        </div>

        <div className="pass-preview-qr">
          <img src={dummyQr} alt="" className="pass-preview-qr-img" />
        </div>
        <div className="pass-preview-qr-altext">{ticketNumber}</div>
      </div>

      {heroImageUrl && (
        <div className="pass-preview-band has-hero pass-preview-membership-banner">
          <img
            src={heroImageUrl}
            alt=""
            className="pass-preview-hero"
            style={{ filter: `brightness(${1 + heroBrightness / 100})` }}
            aria-hidden="true"
          />
        </div>
      )}
    </>
  )
}

// The back is a white scrollable list approximating Google's render:
// logo + title, then a flat sequence of label/value rows for Member, Member ID,
// About this card, House rules, Card progress, Next reward, and the detail
// fields. Links are rendered as separate cards with a per-type icon.
function BackOfPass({
  logoUrl,
  programName,
  accountNameLabel,
  customerName,
  customerEmail,
  passDescription,
  backLinks,
  ownerTerms,
  nextMilestone,
  rewards,
  detailFields,
  referral,
  membership,
}: {
  logoUrl: string | null
  programName: string
  accountNameLabel: string
  customerName: string
  customerEmail: string
  passDescription: string
  backLinks: BackLink[]
  ownerTerms: string
  nextMilestone: PassPreviewReward
  rewards: Array<{ at_stamp: number; reward_text: string }>
  detailFields: PassPreviewDetail[]
  referral: {
    textRows: Array<{ label: string; value: string }>
    shareUrl: string | null
  } | null
  /** SLICE-MEMBERSHIP-PASS — non-null on a membership pass; swaps the loyalty
   *  reward rows for the tier / member-number / benefits rows. */
  membership: {
    tierName: string | null
    memberNumberLabel: string
    memberNumber: string
    memberSince: string
    validUntil: string | null
    benefits: string[]
  } | null
}) {
  // Member ID is a stable per-card identifier on the live pass. We render a
  // ULID-shaped placeholder so the owner can see where it lands; per-card
  // identity isn't a concept inside the program builder.
  const memberId = '01KRWJQORRKPDE2D9PCK3B3JH3'

  return (
    <div className="pass-preview-back">
      <div className="pass-preview-back-top">
        <span className="pass-preview-back-logo">
          {logoUrl ? (
            <img src={logoUrl} alt="" />
          ) : (
            <span className="pass-preview-back-logo-fallback" aria-hidden="true" />
          )}
        </span>
        <h3 className="pass-preview-back-title">{programName || 'Rewards'}</h3>
      </div>

      <BackRow label={accountNameLabel} value={customerName} />
      {/* Member email sits directly under the name — matches the live
          pass's `member-email` textModule. */}
      <BackRow label="Member email" value={customerEmail} />
      {membership ? (
        <>
          {membership.tierName && (
            <BackRow label="Tier" value={membership.tierName} />
          )}
          <BackRow
            label={membership.memberNumberLabel}
            value={membership.memberNumber}
            mono
          />
          <BackRow label="Member since" value={membership.memberSince} />
          {membership.validUntil && (
            <BackRow label="Valid until" value={membership.validUntil} />
          )}
        </>
      ) : (
        <BackRow label="Member ID" value={memberId} mono />
      )}

      {passDescription.trim() && (
        <BackRow label="About this card" value={passDescription} multiline />
      )}
      {ownerTerms.trim() && (
        <BackRow label="House rules" value={ownerTerms} multiline />
      )}

      {/* Loyalty reward rows — the next reward + the full ladder. A membership
          has no stamps/rewards, so these are suppressed and replaced by the
          benefits list below. */}
      {!membership && (
        <>
          {/* No "Card progress" row — the live pass carries the stamp count
              once, via Google's loyaltyPoints block, so the preview matches. */}
          <BackRow
            label={nextMilestone.label}
            value={nextMilestone.text.trim() || 'Your reward appears here'}
          />

          {/* Full reward ladder — mirrors the live pass's `all-rewards`
              textModule. Always shown so the owner sees the full structure
              at design time. The preview is stateless about "claimed" — the
              ✓ prefix only appears on a real wallet pass once a milestone is
              redeemed in the customer's current cycle. */}
          {rewards.length > 0 && (
            <BackRow
              label="All rewards"
              value={[...rewards]
                .sort((a, b) => a.at_stamp - b.at_stamp)
                .map(
                  (r) =>
                    `Stamp ${r.at_stamp}: ${r.reward_text.trim() || 'Your reward appears here'}`,
                )
                .join('\n')}
              multiline
            />
          )}
        </>
      )}

      {/* Membership benefits list — the back-of-pass perks. */}
      {membership && membership.benefits.length > 0 && (
        <BackRow
          label="Benefits"
          value={membership.benefits
            .map((b) => b.trim() || 'Your benefit appears here')
            .join('\n')}
          multiline
        />
      )}

      {detailFields.map((d) => (
        <BackRow key={d.label} label={d.label} value={d.value} />
      ))}

      {/* SLICE-REFERRAL — two sharer blocks (a copyable invite message + the
          mechanics/status board), plus a welcome row on a not-yet-stamped
          referred card. multiline so the \n-separated lines render. */}
      {referral?.textRows.map((r) => (
        <BackRow key={r.label} label={r.label} value={r.value} multiline />
      ))}

      {backLinks.length > 0 && (
        <div className="pass-preview-back-links">
          {backLinks.map((link, i) => (
            <div key={i} className="pass-preview-back-link">
              <span className="pass-preview-back-link-icon" aria-hidden="true">
                {linkIconFor(link.type)}
              </span>
              <span className="pass-preview-back-link-text">
                {link.display_text || link.value}
              </span>
            </div>
          ))}
        </div>
      )}

    </div>
  )
}

function BackRow({
  label,
  value,
  multiline,
  mono,
}: {
  label: string
  value: string
  multiline?: boolean
  mono?: boolean
}) {
  return (
    <div className="pass-preview-back-row">
      <span className="pass-preview-back-row-label">{label}</span>
      <span
        className={`pass-preview-back-row-value${multiline ? ' is-multiline' : ''}${mono ? ' is-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  )
}

// Minimal monoline icons keyed off the BackLink type. currentColor follows
// the surrounding row's text color so it works on light or branded surfaces.
const PhoneIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
  </svg>
)
const GlobeIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </svg>
)
const MailIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
)
const PinIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
)
function linkIconFor(type: BackLink['type']) {
  switch (type) {
    case 'phone':
      return PhoneIcon
    case 'email':
      return MailIcon
    case 'address':
      return PinIcon
    case 'url':
    case 'facebook':
    case 'instagram':
    case 'tiktok':
    default:
      return GlobeIcon
  }
}

// Memoized on the program prop — the wizard re-renders a lot as the owner
// types; the parent debounces the program object so this only re-renders when
// the relevant slice of state actually settles.
export const PassPreview = memo(PassPreviewImpl)
